// Builds an acquisition/gear view model from the AstroBin snapshot fields
// (image.acquisition, image.integration_seconds, image.gear). Used as the
// automatic fallback when an image has no hand-entered Firestore override.

const FILTER_SHORTCUTS = [
    [/h[\s-]?alpha|\bh[\s-]?a\b|ha\b/i, 'Ha'],
    [/oxygen\s*iii|\bo[\s-]?iii\b|\boiii\b/i, 'OIII'],
    [/sulfur\s*ii|sulphur\s*ii|\bs[\s-]?ii\b|\bsii\b/i, 'SII'],
    [/l[\s-]?extreme/i, 'L-eXtreme'],
    [/l[\s-]?enhance/i, 'L-eNhance'],
    [/luminance|\blum\b|\bl\b/i, 'L'],
    [/\bred\b|\br\b/i, 'R'],
    [/\bgreen\b|\bg\b/i, 'G'],
    [/\bblue\b|\bb\b/i, 'B'],
    [/uv[\s/-]?ir|uv\s*ir\s*cut/i, 'UV/IR']
];

const shortenFilter = (name) => {
    if (!name) return 'Unfiltered';
    for (const [pattern, label] of FILTER_SHORTCUTS) {
        if (pattern.test(name)) return label;
    }
    // Fall back to the last word of the filter name (often the useful bit).
    return name.split(/\s+/).slice(-2).join(' ');
};

const formatExposure = (seconds) => {
    const value = Number(seconds) || 0;
    if (value >= 60 && value % 60 === 0) return `${value / 60}m`;
    return `${value}s`;
};

export const formatDuration = (totalSeconds) => {
    const normalizedSeconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const hours = Math.floor(normalizedSeconds / 3600);
    const minutes = Math.floor((normalizedSeconds % 3600) / 60);
    const seconds = normalizedSeconds % 60;
    const parts = [];

    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

    return parts.join(' ');
};

export const GEAR_GROUPS = [
    { key: 'telescopes', heading: 'Telescope / Lens', card: 'imaging' },
    { key: 'cameras', heading: 'Camera', card: 'imaging' },
    { key: 'mounts', heading: 'Mount', card: 'imaging' },
    { key: 'filters', heading: 'Filters', card: 'imaging' },
    { key: 'guiding_telescopes', heading: 'Guiding Scope', card: 'guiding' },
    { key: 'guiding_cameras', heading: 'Guiding Camera', card: 'guiding' },
    { key: 'software', heading: 'Software', card: 'software' },
    { key: 'accessories', heading: 'Accessories', card: 'accessories' }
];

export const buildAstrobinAcquisition = (image) => {
    const acquisition = Array.isArray(image?.acquisition) ? image.acquisition : [];
    const gear = image?.gear && typeof image.gear === 'object' ? image.gear : {};

    const integrationEntries = acquisition
        .filter((entry) => entry.frames && entry.exposure_seconds)
        .map((entry) => ({
            filterShortLabel: shortenFilter(entry.filter),
            filterName: entry.filter || 'Unfiltered',
            pattern: `${entry.frames} × ${formatExposure(entry.exposure_seconds)}`,
            totalSeconds: entry.total_seconds || entry.frames * entry.exposure_seconds
        }));

    const totalIntegrationSeconds = image?.integration_seconds
        || integrationEntries.reduce((sum, e) => sum + e.totalSeconds, 0);

    const gearCards = { imaging: [], guiding: [], software: [], accessories: [] };
    for (const group of GEAR_GROUPS) {
        const items = gear[group.key];
        if (Array.isArray(items) && items.length) {
            gearCards[group.card].push({ heading: group.heading, items });
        }
    }

    const hasContent = integrationEntries.length > 0
        || Object.values(gearCards).some((rows) => rows.length > 0);

    return { integrationEntries, totalIntegrationSeconds, gearCards, hasContent };
};
